import React, { useCallback } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useSelector } from 'react-redux'
import SvgAttachmentFile from 'src/components/Svgs/SvgAttachmentFile'
import { useTranslate } from 'src/localization/Trns'
import { selectIsPushingAttachmentsToERPFromState } from 'src/redux/selectors/data/receiptAttachments.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { css } from 'styled-components'

// Figma: Receipt-Image-Verification flyout — wide dropzone above receipts.
// Full-width dashed container with centered upload icon and two-line copy.

const wideDropzoneStyle = css`
  &.WideDropzone {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px 16px;
    border: 1px dashed #dadbe0;
    background-color: #f7f8fb;
    border-radius: 8px;
    cursor: pointer;
    transition:
      background-color 120ms ease-out,
      border-color 120ms ease-out;
    font-family: Rubik, sans-serif;

    &:hover {
      border-color: #4489f9;
      background-color: #f0f5ff;
    }

    .dropzone-input:focus {
      outline: none;
    }

    .primary {
      font-size: 14px;
      line-height: 20px;
      color: #1e2c4c;
    }

    .primary .link {
      color: #4489f9;
      text-decoration: underline;
      cursor: pointer;
    }

    .secondary {
      font-size: 12px;
      line-height: 16px;
      color: #697080;
    }

    &.disabled {
      pointer-events: none;
      opacity: 0.5;
    }
  }
`

interface WideDropzoneProps {
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  setAttachmentError?: (
    errorMsg: string | React.ReactElement | undefined,
  ) => void
  setErrors?: (onlyOneReceipt: boolean) => void
  attachmentLimit?: number
  attachmentsLength?: number
}

export default function WideDropzone({
  uploadAttachments,
  setAttachmentError,
  setErrors,
  attachmentLimit,
  attachmentsLength,
}: WideDropzoneProps): React.ReactElement {
  const translate = useTranslate()
  const isPushingToERP = useSelector(selectIsPushingAttachmentsToERPFromState)

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]): void => {
      setAttachmentError?.(undefined)

      if (
        attachmentLimit !== undefined &&
        attachmentsLength !== undefined &&
        attachmentsLength + acceptedFiles.length + fileRejections.length >
          attachmentLimit
      ) {
        setAttachmentError?.(
          translate('Only ${numImages} images can be uploaded at a time', {
            numImages: attachmentLimit.toString(),
          }),
        )
        return
      }

      const attachments = acceptedFiles.map((file) => {
        const form = new FormData()
        form.append('file', file, file.name)
        const thumbnailUrl = URL.createObjectURL(file)
        return { formData: form, uploadId: thumbnailUrl, thumbnailUrl }
      })
      uploadAttachments(attachments)
      if (fileRejections.length > 0) {
        setAttachmentError?.(translate('Files cannot exceed 10MB'))
      }
      setErrors?.(false)
    },
    [
      uploadAttachments,
      attachmentLimit,
      attachmentsLength,
      setAttachmentError,
      setErrors,
      translate,
    ],
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/tiff': ['.tiff', '.tif'],
      'image/gif': ['.gif'],
      'image/bmp': ['.bmp'],
      'image/heic': ['.heic'],
      'image/svg+xml': ['.svg'],
    },
    maxSize: 10000000,
  })

  return (
    <div
      css={wideDropzoneStyle}
      className={`WideDropzone${isPushingToERP ? ' disabled' : ''}`}
      data-testid="add-attachment"
      {...getRootProps()}
    >
      <input className="dropzone-input" {...getInputProps()} />
      <SvgAttachmentFile />
      <span className="primary">
        <span className="link">{translate('Click to upload')}</span>{' '}
        {translate('or drag and drop')}
      </span>
      <span className="secondary">
        {translate('PDF, SVG, PNG, JPG or GIF (max. 10MB)')}
      </span>
    </div>
  )
}
